import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {Observable} from 'rxjs';
import {HttpUtils} from '../utils/http-utils';
import {Page} from '../domain/page.model';
import {AdServerTypeMacros, Placements} from '../domain/placements.model';
import {Targeting} from '../domain/targeting.model';
import {urlJoin} from 'url-join-ts';
import {IFilters} from '../interfaces/filters.model';
import { EndpointGeneratorRequest } from '../domain/endpoint-generator.model';
import { shareReplay, tap } from 'rxjs/operators';
import { MixpanelService } from './mixpanel.service';
import { MixpanelEvents } from '../analytics/mixpanel-events';
import { withMixpanelOutcome } from '../analytics/mixpanel-tracking.utils';

@Injectable({
  providedIn: 'root',
})
export class PlacementsService {
  private readonly endpoint = 'api/placements';

  constructor(
    private httpClient: HttpClient,
    private mixpanel: MixpanelService
  ) {
  }

  listPlacements(filter: any): Observable<Page<Placements>> {
    return this.httpClient.get<Page<Placements>>(`${environment.serviceUrl}/api/placements`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  addPlacement(placement: any) {
    return this.httpClient
      .post(`${environment.serviceUrl}/api/placements`, placement)
      .pipe(
        withMixpanelOutcome(this.mixpanel, {
          successEvent: MixpanelEvents.PlacementCreated,
          failureEvent: MixpanelEvents.PlacementCreateFailed,
          successProps: (res: any) => ({
            placement_id: res?.id,
            placement_name: res?.name,
          }),
          failureProps: () => ({
            placement_name: placement?.name,
          }),
        })
      );
  }

  updatePlacement(placement: any, id: string) {
    return this.httpClient.put(`${environment.serviceUrl}/api/placements/${id}`, placement);
  }

  getPlacementById(id): Observable<Placements> {
    return this.httpClient.get<Placements>(`${environment.serviceUrl}/api/placements/${id}`);
  }

  getStatusCount(filters: any) {
    return this.httpClient.get(`${environment.serviceUrl}/api/placements/statuses`, {
      params: HttpUtils.getHttpParams(filters)
    });
  }

  public getPlacementTargeting(id: string, filters: IFilters): Observable<Page<Targeting>> {
    return this.httpClient.get<Page<Targeting>>(urlJoin(environment.serviceUrl, this.endpoint, id, 'targeting'), {
      params: HttpUtils.getHttpParams(filters)
    });
  }

  archivePlacement(id: string) {
    return this.httpClient.put(`${environment.serviceUrl}/api/placements/${id}/archive`, {});
  }

  unarchivePlacement(id: string) {
    return this.httpClient.put(`${environment.serviceUrl}/api/placements/${id}/unarchive`, {});
  }

  //Endpoint generator
  public generateEndpointTag(request: EndpointGeneratorRequest) {
    return this.httpClient.post(`${environment.serviceUrl}/api/endpoint-generator/vbx-tag-generator`, request,
      {responseType: 'text'}
    );
  }

  public generateVBXTag(endpoint: string, source: string) {
    return this.httpClient.post(`${environment.serviceUrl}/api/endpoint-generator/vbx-connection-generator`, {endpoint, source},
      {responseType: 'text'}
    );
  }

  public getMacrosList(filter: any) {
    return this.httpClient.get<AdServerTypeMacros[]>(`${environment.serviceUrl}/api/endpoint-generator/macros-ad-server`, {
      params: HttpUtils.getHttpParams(filter)
    });
  }

  public getVBXMacrosList() {
    return this.httpClient.get<AdServerTypeMacros[]>(`${environment.serviceUrl}/api/endpoint-generator/macros-vbx`);
  }

  private placementsAdFormats$?: Observable<PlacementsAdFormatType[]>;

  public getPlacementAdTypes(): Observable<PlacementsAdFormatType[]> {
    if (!this.placementsAdFormats$) {
      this.placementsAdFormats$ = this.httpClient
        .get<PlacementsAdFormatType[]>(`${environment.serviceUrl}/api/placements/ad-formats`)
        .pipe(
          shareReplay(1),
          tap((data)=>{
            data.forEach((item) => {
              if (item.type === AdFormatType.CTV_SLATE) {
                item.label = 'Slates';
              } else {
                item.label = item.type
                  .toLowerCase()
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, char => char.toUpperCase());
              }
            });
          }));
    }
    return this.placementsAdFormats$;
  }
}

export interface PlacementsAdFormatType {
  id: string;
  type: AdFormatType;
  label: string;
}

export enum AdFormatType {
  STANDARD_LINEAR_AD = 'STANDARD_LINEAR_AD',
  INTERACTIVE_LINEAR_AD = 'INTERACTIVE_LINEAR_AD',
  NON_LINEAR_FULL_SCREEN_AD = 'NON_LINEAR_FULL_SCREEN_AD',
  NON_LINEAR_PARTIAL_SCREEN_AD = 'NON_LINEAR_PARTIAL_SCREEN_AD',
  CTV_SLATE = 'CTV_SLATE'
}
